/**
 * Messages: conversation and message schemas, mappers to `Thread` / `Message`.
 * Endpoints: /api/conversations/my?channel=all, /api/conversations (support find-or-create),
 * /api/conversations/:id/messages, /:id/read, /:id/bot-response, /:id/rate. Live updates arrive through
 * services/realtime (Socket.IO); the same mappers turn its payloads into domain messages.
 */
import { format, isToday } from 'date-fns';
import { z } from 'zod';
import { ns, translate } from '@/i18n';
import { toISODate } from '@/lib/clock';
import { formatDayMonth, formatDayShort } from '@/lib/format';
import { SUPPORT_DRAFT_ID, type Message, type Thread } from '../../domain';
import { utcDay } from './bookings';

const s = z.string().nullish();
const n = z.number().nullish();

const ButtonSchema = z.object({ id: z.string(), label: z.string(), action: s }).passthrough();
export const MessageSchema = z
  .object({
    _id: z.string(),
    senderModel: s,
    content: z.string().default(''),
    contentType: s,
    createdAt: s,
    metadata: z
      .object({ buttons: z.array(ButtonSchema).nullish(), selectedButtonId: s, systemAction: s })
      .passthrough()
      .nullish(),
  })
  .passthrough();
export type MessageRecord = z.infer<typeof MessageSchema>;
export const MessagePageSchema = z
  .object({ messages: z.array(MessageSchema).default([]), hasMore: z.boolean().nullish() })
  .passthrough();

const ParticipantRef = z.union([z.string(), z.object({ _id: z.string() }).passthrough()]);
export const ConversationSchema = z
  .object({
    _id: z.string(),
    channel: s,
    status: s,
    botState: z.object({ step: s }).passthrough().nullish(),
    agency: z.union([z.string(), z.object({ _id: z.string(), agencyName: s }).passthrough()]).nullish(),
    reservation: z
      .union([
        z.string(),
        z
          .object({
            _id: z.string(),
            startDate: s,
            returnDate: s,
            car: z.union([z.string(), z.object({ brand: s, subBrand: s, city: s }).passthrough()]).nullish(),
          })
          .passthrough(),
      ])
      .nullish(),
    unreadCounts: z
      .array(z.object({ participantModel: s, participant: ParticipantRef, count: n }).passthrough())
      .nullish(),
    lastMessage: z.object({ content: s, sentAt: s }).passthrough().nullish(),
    satisfactionRating: n,
    /** Agency threads: closed once `closesAt` has passed (D5); `canWrite` only on `GET /:id`. */
    agencyParty: z.object({ closesAt: s }).passthrough().nullish(),
    canWrite: z.boolean().nullish(),
    updatedAt: s,
    createdAt: s,
  })
  .passthrough();
export type ConversationRecord = z.infer<typeof ConversationSchema>;
export const ConversationListSchema = z.array(ConversationSchema);

const refId = (v: z.infer<typeof ParticipantRef>) => (typeof v === 'string' ? v : v._id);

/** «09:12» today, «12 nov.» (en «12 Nov») before. */
export function threadTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return isToday(d) ? format(d, 'HH:mm') : formatDayMonth(d);
}

export function toMessage(m: MessageRecord): Message {
  const d = m.createdAt ? new Date(m.createdAt) : new Date();
  return {
    id: m._id,
    from: m.senderModel === 'client' ? 'me' : 'them',
    text: m.content,
    time: format(d, 'HH:mm'),
    date: toISODate(d),
  };
}

/**
 * Messages to show, oldest first. A bot question the client answered is followed by the chosen option as
 * the client's own bubble (the current site marked the chosen button; the design has no buttons in bubbles).
 */
export function toMessages(records: MessageRecord[]): Message[] {
  return records
    .filter((m) => m.content.trim())
    .flatMap((m) => {
      const shown = toMessage(m);
      const chosen = m.metadata?.selectedButtonId;
      const button = chosen ? m.metadata?.buttons?.find((b) => b.id === chosen) : undefined;
      if (!button) return [shown];
      const text = m.metadata?.systemAction === 'bot_rating' ? `${button.label} / 5` : button.label;
      return [shown, { ...shown, id: `${m._id}:choice`, from: 'me' as const, text }];
    });
}

/** The latest bot question still waiting for an answer, or null. */
export function pendingBotPrompt(c: ConversationRecord, messages: MessageRecord[]): MessageRecord | null {
  if (c.status !== 'bot_flow') return null;
  const prompt = [...messages].reverse().find((m) => m.metadata?.buttons?.length);
  if (!prompt || prompt.metadata?.selectedButtonId) return null;
  return prompt;
}

/** The choices of the support bot still waiting for an answer (latest prompt, not yet answered). */
export function pendingBotButtons(c: ConversationRecord, messages: MessageRecord[]) {
  return pendingBotPrompt(c, messages)?.metadata?.buttons ?? [];
}

/** A support conversation people can still write in (not resolved or archived). */
export const isLiveSupport = (c: ConversationRecord) =>
  c.channel !== 'agency' && c.status !== 'resolved' && c.status !== 'archived';

/** Agency thread still open (the backend's `isAgencyThreadOpen`: not archived, `closesAt` not passed). */
export function agencyThreadOpen(c: ConversationRecord, now = new Date()): boolean {
  if (typeof c.canWrite === 'boolean') return c.canWrite;
  if (c.status === 'archived') return false;
  const closesAt = c.agencyParty?.closesAt ? new Date(c.agencyParty.closesAt) : null;
  return !closesAt || Number.isNaN(closesAt.getTime()) || now <= closesAt;
}

/** Unread messages of this client in the conversation. */
export function unreadFor(c: ConversationRecord, clientId: string): number {
  return (c.unreadCounts ?? [])
    .filter((u) => u.participantModel === 'client' && refId(u.participant) === clientId)
    .reduce((sum, u) => sum + Math.max(0, Number(u.count) || 0), 0);
}

/** The support thread before it exists on the server: its first message creates it. */
export function supportDraftThread(): Thread {
  return {
    id: SUPPORT_DRAFT_ID,
    kind: 'support',
    name: translate(ns.labels, 'thread.support'),
    subtitle: translate(ns.labels, 'thread.supportSubtitle'),
    initials: 'C',
    unread: false,
    unreadCount: 0,
    time: '',
    messages: [],
    state: 'open',
    draft: true,
  };
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

export function toThread(
  c: ConversationRecord,
  messages: MessageRecord[],
  clientId: string,
  options: { hasMore?: boolean; now?: Date } = {},
): Thread {
  const agency = c.channel === 'agency';
  const agencyName = c.agency && typeof c.agency === 'object' ? (c.agency.agencyName ?? '') : '';
  const resv = c.reservation && typeof c.reservation === 'object' ? c.reservation : null;
  const car = resv?.car && typeof resv.car === 'object' ? resv.car : null;
  const carName = [car?.brand, car?.subBrand].filter(Boolean).join(' ');
  const name = agency ? agencyName || translate(ns.labels, 'partnerAgency') : translate(ns.labels, 'thread.support');
  const unreadCount = unreadFor(c, clientId);
  const last = messages[messages.length - 1];
  const prompt = pendingBotPrompt(c, messages);
  const buttons = prompt?.metadata?.buttons ?? [];
  const state: Thread['state'] = agency
    ? agencyThreadOpen(c, options.now)
      ? 'open'
      : 'closed'
    : c.status === 'bot_flow'
      ? 'bot'
      : c.status === 'resolved' || c.status === 'archived'
        ? 'resolved'
        : 'open';
  return {
    id: c._id,
    kind: agency ? 'agency' : 'support',
    name,
    subtitle: agency
      ? [
          carName,
          resv?.startDate
            ? `${formatDayShort(utcDay(resv.startDate))} → ${formatDayShort(utcDay(resv.returnDate))}`
            : '',
        ]
          .filter(Boolean)
          .join(' · ') || translate(ns.labels, 'thread.yourBooking')
      : translate(ns.labels, 'thread.supportSubtitle'),
    initials: agency ? initialsOf(name) || 'AP' : 'C',
    unread: unreadCount > 0,
    unreadCount,
    time: threadTime(last?.createdAt ?? c.lastMessage?.sentAt ?? c.updatedAt),
    messages: toMessages(messages),
    quickReplies: buttons.length ? buttons.map((x) => x.label) : undefined,
    state,
    ratingPrompt: prompt?.metadata?.systemAction === 'bot_rating' || undefined,
    rated: agency ? undefined : typeof c.satisfactionRating === 'number' && c.satisfactionRating > 0,
    hasMore: options.hasMore ?? false,
  };
}
